import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
    Alert,
    Chip,
    Stack,
    useMediaQuery,
    Card,
    CardContent,
    Grid
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon, Balance as BalanceIcon } from '@mui/icons-material';
import { StyledTableCell, AmountTypography } from 'views/ConstantColors';
import secureLocalStorage from 'react-secure-storage';
import { useTravelRequest } from 'hooks/api/travel-request/useTravelRequest';

// Utility function to format amounts consistently
const formatAmount = (amount) => parseFloat(amount || 0).toFixed(2);

// Calculate totals from an array of expense data
const calculateCategoryTotals = (data) =>
    data.reduce(
        (totals, row) => ({
            breakfast: totals.breakfast + parseFloat(row.breakfast || 0),
            lunch: totals.lunch + parseFloat(row.lunch || 0),
            dinner: totals.dinner + parseFloat(row.dinner || 0),
            incidental: totals.incidental + parseFloat(row.incidental || 0),
            miscAmt: totals.miscAmt + parseFloat(row.miscAmt || 0),
            accommodation: totals.accommodation + parseFloat(row.accommodation || 0)
        }),
        {
            breakfast: 0,
            lunch: 0,
            dinner: 0,
            incidental: 0,
            miscAmt: 0,
            accommodation: 0
        }
    );

// Calculate grand total from category totals
const calculateGrandTotal = (totals) => Object.values(totals).reduce((sum, value) => sum + value, 0);

// Format line activity data from API to match our expense data format
const formatLineActivity = (item) => ({
    id: item.id,
    date: item.activityDate || new Date().toISOString().split('T')[0],
    breakfast: item.breakfast || 0,
    lunch: item.lunch || 0,
    dinner: item.dinner || 0,
    incidental: item.incidental || 0,
    miscDesc: item.miscellaneous || '',
    miscAmt: 0, // Assuming this is included in the original data
    accommodation: item.accommodation || 0
});

const TotalComparisonDialog = ({ open, onClose, onProceed, requestId, showActions = true }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { lineActivities, getLineActivities } = useTravelRequest();
    const [originalData, setOriginalData] = useState([]);
    const [currentData, setCurrentData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const getVarianceColor = (diff) => {
        if (diff > 0) return 'success.main';
        if (diff < 0) return 'error.main';
        return 'inherit';
    };

    const getGrandVarianceColor = (diff) => {
        if (diff > 0) return 'success.main';
        if (diff < 0) return 'error.main';
        return 'primary.dark';
    };

    const getVarianceSeverity = (diff) => {
        if (diff < 0) return 'error';
        if (diff > 0) return 'warning';
        return 'info';
    };

    // Load data
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                // Get current expense data from localStorage
                const savedData = secureLocalStorage.getItem('expenseData');
                if (savedData) {
                    setCurrentData(JSON.parse(savedData));
                }

                // Get original data from API
                if (!lineActivities || lineActivities.length === 0) {
                    try {
                        await getLineActivities(requestId);
                    } catch (err) {
                        console.error('Failed to fetch line activities:', err);
                        setError('Failed to load original expense data. Please try again.');
                    }
                }

                // Format the original data if available
                if (lineActivities && lineActivities.length > 0) {
                    const formattedOriginalData = lineActivities.map(formatLineActivity);
                    setOriginalData(formattedOriginalData);

                    // If we don't have current data yet, use original as fallback
                    if (!savedData) {
                        setCurrentData(formattedOriginalData);
                    }
                } else if (!savedData) {
                    // No original data and no saved data
                    setError('No expense data available. Please add expenses before proceeding.');
                }
            } catch (err) {
                console.error('Failed to load comparison data:', err);
                setError('Failed to load comparison data. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        if (open) {
            loadData();
        }
    }, [open, requestId, lineActivities, getLineActivities]);

    // Calculate totals
    const originalTotals = calculateCategoryTotals(originalData);
    const currentTotals = calculateCategoryTotals(currentData);

    const originalGrandTotal = calculateGrandTotal(originalTotals);
    const currentGrandTotal = calculateGrandTotal(currentTotals);

    const difference = currentGrandTotal - originalGrandTotal;
    const isDifferent = Math.abs(difference) > 0.01; // Use a small epsilon for floating point comparison

    // Determine traveler balance status
    const getTravelerStatus = () => {
        if (!isDifferent) {
            return {
                status: 'BALANCED',
                message: 'Traveler is balanced',
                color: 'primary',
                icon: <BalanceIcon />,
                description: 'No reimbursement needed'
            };
        }

        if (currentGrandTotal < originalGrandTotal) {
            // Spent less than budgeted
            return {
                status: 'TRAVELER OWES',
                message: `Traveler owes $${formatAmount(Math.abs(difference))}`,
                color: 'error',
                icon: <TrendingDownIcon />,
                description: 'Traveler needs to return unused funds'
            };
        }

        // Spent more than budgeted
        return {
            status: 'TRAVELER IS OWED',
            message: `Traveler is owed $${formatAmount(difference)}`,
            color: 'success',
            icon: <TrendingUpIcon />,
            description: 'Traveler needs to be reimbursed'
        };
    };

    const travelerStatus = getTravelerStatus();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                elevation: 3,
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle>
                <Typography variant="h5" component="div" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    Total Cost Comparison
                </Typography>
            </DialogTitle>

            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography>Loading comparison data...</Typography>
                    </Box>
                ) : (
                    <>
                        {/* Status Banner */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                p: 2,
                                mb: 3,
                                borderRadius: 2,
                                bgcolor: `${travelerStatus.color}.lighter`,
                                border: 1,
                                borderColor: `${travelerStatus.color}.main`
                            }}
                        >
                            <Chip
                                icon={travelerStatus.icon}
                                label={travelerStatus.status}
                                color={travelerStatus.color}
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: '1rem',
                                    mb: 1
                                }}
                            />
                            <Typography variant="h6" color={`${travelerStatus.color}.dark`} sx={{ fontWeight: 600 }}>
                                {travelerStatus.message}
                            </Typography>
                            <Typography variant="body2" color={`${travelerStatus.color}.dark`}>
                                {travelerStatus.description}
                            </Typography>
                        </Box>

                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Please review the changes in your trip expenses before proceeding:
                        </Typography>

                        {isMobile ? (
                            <Stack spacing={2} sx={{ mb: 3 }}>
                                {Object.entries(originalTotals).map(([category, originalAmount]) => {
                                    const currentAmount = currentTotals[category];
                                    const categoryDiff = currentAmount - originalAmount;
                                    const displayName = category.charAt(0).toUpperCase() + category.slice(1);

                                    return (
                                        <Card key={category} variant="outlined" sx={{ borderRadius: 2 }}>
                                            <CardContent sx={{ p: 2 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main' }}>
                                                    {displayName}
                                                </Typography>
                                                <Grid container spacing={1}>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Budget Amount
                                                        </Typography>
                                                        <Typography variant="body2">${formatAmount(originalAmount)}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Spent Amount
                                                        </Typography>
                                                        <Typography variant="body2">${formatAmount(currentAmount)}</Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Divider sx={{ my: 1 }} />
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center'
                                                            }}
                                                        >
                                                            <Typography variant="subtitle2">Variance:</Typography>
                                                            <Typography
                                                                variant="subtitle2"
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    color: getVarianceColor(categoryDiff)
                                                                }}
                                                            >
                                                                {categoryDiff > 0 ? '+' : ''}
                                                                {formatAmount(categoryDiff)}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    );
                                })}

                                <Card
                                    sx={{
                                        bgcolor: 'primary.lighter',
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: 'primary.main'
                                    }}
                                >
                                    <CardContent sx={{ p: 2 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.dark', mb: 2 }}>
                                            GRAND TOTAL COMPARISON
                                        </Typography>
                                        <Stack spacing={1}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2">Total Budget:</Typography>
                                                <Typography variant="subtitle2">${formatAmount(originalGrandTotal)}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2">Total Spent:</Typography>
                                                <Typography variant="subtitle2">${formatAmount(currentGrandTotal)}</Typography>
                                            </Box>
                                            <Divider sx={{ my: 1, borderColor: 'primary.main' }} />
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                                                    TOTAL VARIANCE:
                                                </Typography>
                                                <Typography
                                                    variant="h5"
                                                    sx={{
                                                        fontWeight: 800,
                                                        color: getGrandVarianceColor(difference)
                                                    }}
                                                >
                                                    {difference > 0 ? '+' : ''}
                                                    {formatAmount(difference)}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Stack>
                        ) : (
                            <TableContainer component={Paper} sx={{ mb: 3 }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>Expense Category</StyledTableCell>
                                            <StyledTableCell align="right">Budget Amount ($)</StyledTableCell>
                                            <StyledTableCell align="right">Spent Amount ($)</StyledTableCell>
                                            <StyledTableCell align="right">Variance ($)</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.entries(originalTotals).map(([category, originalAmount]) => {
                                            const currentAmount = currentTotals[category];
                                            const categoryDiff = currentAmount - originalAmount;
                                            const displayName = category.charAt(0).toUpperCase() + category.slice(1);

                                            return (
                                                <TableRow key={category}>
                                                    <TableCell>{displayName}</TableCell>
                                                    <TableCell align="right">
                                                        <AmountTypography>{formatAmount(originalAmount)}</AmountTypography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <AmountTypography>{formatAmount(currentAmount)}</AmountTypography>
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            color: getVarianceColor(categoryDiff)
                                                        }}
                                                    >
                                                        {categoryDiff > 0 ? '+' : ''}
                                                        {formatAmount(categoryDiff)}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}

                                        {/* Total row */}
                                        <TableRow sx={{ backgroundColor: 'primary.lighter' }}>
                                            <TableCell sx={{ fontWeight: 700 }}>GRAND TOTAL</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                ${formatAmount(originalGrandTotal)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                ${formatAmount(currentGrandTotal)}
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: getVarianceColor(difference)
                                                }}
                                            >
                                                {difference > 0 ? '+' : ''}
                                                {formatAmount(difference)}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {isDifferent && (
                            <Alert
                                severity={getVarianceSeverity(difference)}
                                sx={{ mb: 2 }}
                                icon={difference < 0 ? <TrendingDownIcon /> : <TrendingUpIcon />}
                            >
                                {difference < 0
                                    ? `Traveler spent $${formatAmount(
                                          Math.abs(difference)
                                      )} less than budgeted. Traveler must return unused funds.`
                                    : `Traveler spent $${formatAmount(difference)} more than budgeted. Traveler requires reimbursement.`}
                                {difference > 0 &&
                                    currentData.length > originalData.length &&
                                    ` This includes ${currentData.length - originalData.length} additional day(s) of expenses.`}
                            </Alert>
                        )}

                        {!isDifferent && (
                            <Alert severity="info" icon={<BalanceIcon />} sx={{ mb: 2 }}>
                                Expenses match budget exactly (${formatAmount(originalGrandTotal)}). No reimbursement needed.
                            </Alert>
                        )}

                        {isDifferent && (
                            <Box
                                sx={{
                                    mt: 3,
                                    p: 2,
                                    bgcolor: 'background.paper',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                }}
                            >
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                                    Summary of Changes:
                                </Typography>
                                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                                    {originalData.length !== currentData.length && (
                                        <li>
                                            {currentData.length > originalData.length
                                                ? `Added ${currentData.length - originalData.length} new expense day(s)`
                                                : `Removed ${originalData.length - currentData.length} expense day(s)`}
                                        </li>
                                    )}
                                    {Object.entries(originalTotals)
                                        .map(([category, originalAmount]) => {
                                            const currentAmount = currentTotals[category];
                                            const categoryDiff = currentAmount - originalAmount;

                                            if (Math.abs(categoryDiff) > 0.01) {
                                                const displayName = category.charAt(0).toUpperCase() + category.slice(1);
                                                return (
                                                    <li key={category}>
                                                        {categoryDiff > 0
                                                            ? `Increased ${displayName.toLowerCase()} expenses by $${formatAmount(
                                                                  categoryDiff
                                                              )}`
                                                            : `Decreased ${displayName.toLowerCase()} expenses by $${formatAmount(
                                                                  Math.abs(categoryDiff)
                                                              )}`}
                                                    </li>
                                                );
                                            }
                                            return null;
                                        })
                                        .filter(Boolean)}
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>

            {showActions && (
                <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                    <Button onClick={onClose} variant="outlined">
                        Back to Edit
                    </Button>
                    <Button onClick={onProceed} variant="contained" color="primary" disabled={loading}>
                        Proceed to Next Step
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

TotalComparisonDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onProceed: PropTypes.func.isRequired,
    requestId: PropTypes.string.isRequired,
    showActions: PropTypes.bool
};

export default TotalComparisonDialog;
